import { useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import {
  CARRY_OVER_ANCHORS,
  DEFAULT_TOP_OFFSET_MM,
  PRODUCT_CLASSES,
  defaultAnchorForClass,
  isCarryOverAnchor,
  isProductClass,
  isValidCarryOverPercent,
  type CarryOverAnchor,
  type ProductClass,
} from '@designdeck/shared';
import {
  Button,
  Card,
  CardHeader,
  CardTitle,
  FormField,
  Input,
  Select,
} from '@/components/ui';
import { getErrorMessage } from '@/api';
import type { AdminProduct, SetProductCarryOverPayload } from '@/api/admin';
import {
  useCarryOverImpact,
  useSetProductCarryOver,
} from '@/hooks/useAdminProducts';

export const PRODUCT_CLASS_LABEL: Record<ProductClass, string> = {
  TEXTILE: 'Textile',
  OBJECT: 'Object',
};

export const CARRY_OVER_ANCHOR_LABEL: Record<CarryOverAnchor, string> = {
  TOP: 'top',
  CENTER: 'centred',
};

/** The form as typed: empty strings stand for "not set". */
interface CarryOverForm {
  productClass: ProductClass | '';
  percent: string;
  anchor: CarryOverAnchor | '';
  offsetMm: string;
}

function formOf(product: AdminProduct): CarryOverForm {
  return {
    productClass: product.productClass ?? '',
    percent:
      product.carryOverPercent != null ? String(product.carryOverPercent) : '',
    anchor: product.carryOverAnchor ?? '',
    offsetMm:
      product.carryOverTopOffsetMm != null
        ? String(product.carryOverTopOffsetMm)
        : '',
  };
}

function formsEqual(a: CarryOverForm, b: CarryOverForm): boolean {
  return (
    a.productClass === b.productClass &&
    a.percent === b.percent &&
    a.anchor === b.anchor &&
    a.offsetMm === b.offsetMm
  );
}

const TOAST_OPTS = { position: 'bottom-right' as const };

/** The default anchor the rule applies when the product sets none. */
export function anchorPlaceholder(productClass: ProductClass | null): string {
  const anchor = CARRY_OVER_ANCHOR_LABEL[defaultAnchorForClass(productClass)];
  const scope = productClass
    ? `${PRODUCT_CLASS_LABEL[productClass].toLowerCase()} default`
    : 'default without a class';
  return `${anchor} (${scope})`;
}

interface ProductCarryOverPanelProps {
  product: AdminProduct;
}

/**
 * Product-level carry-over: class, percent box, vertical anchor and top
 * offset. These are the product values; a print area without an exception
 * inherits them (the exceptions live in the area sidebar). The impact
 * counter says how many stored positions the values reach before saving.
 */
export function ProductCarryOverPanel({ product }: ProductCarryOverPanelProps) {
  const [form, setForm] = useState<CarryOverForm>(() => formOf(product));
  // Memoised on the four values, not on the product object: a refetch that
  // only changed print areas must not count as a new baseline.
  const baseline = useMemo(
    () => formOf(product),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      product.productClass,
      product.carryOverPercent,
      product.carryOverAnchor,
      product.carryOverTopOffsetMm,
    ]
  );
  const lastBaseline = useRef(baseline);
  const impact = useCarryOverImpact(product.id);
  const save = useSetProductCarryOver(product.id);

  // A refetched product (after a save, or another admin's write) is the new
  // baseline; a form the admin is editing is left alone until it is saved.
  useEffect(() => {
    const previous = lastBaseline.current;
    lastBaseline.current = baseline;
    setForm((current) => (formsEqual(current, previous) ? baseline : current));
  }, [baseline]);

  const percentValue = form.percent === '' ? null : Number(form.percent);
  const percentError =
    percentValue !== null && !isValidCarryOverPercent(percentValue)
      ? 'Whole number between 1 and 100.'
      : undefined;
  const offsetValue = form.offsetMm === '' ? null : Number(form.offsetMm);
  const offsetError =
    offsetValue !== null && !(Number.isInteger(offsetValue) && offsetValue >= 0)
      ? 'Whole number of millimetres, 0 or more.'
      : undefined;

  const dirty = !formsEqual(form, baseline);
  const canSave = dirty && !percentError && !offsetError && !save.isPending;
  const selectedClass: ProductClass | null = form.productClass || null;

  const handleSave = async () => {
    const payload: SetProductCarryOverPayload = {
      productClass: selectedClass,
      carryOverPercent: percentValue,
      carryOverAnchor: form.anchor || null,
      carryOverTopOffsetMm: offsetValue,
    };
    try {
      await save.mutateAsync(payload);
      toast.success('Carry-over saved', TOAST_OPTS);
    } catch (error) {
      toast.error(getErrorMessage(error), TOAST_OPTS);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Carry-over</CardTitle>
      </CardHeader>
      <p className="text-xs text-mute">
        How a design made on another product lands on this one. These are the
        product values; print areas without an exception inherit them.
      </p>

      <div className="mt-4 grid grid-cols-2 gap-4 md:grid-cols-4">
        <FormField
          label="Class"
          hint={
            selectedClass
              ? undefined
              : `Unknown${
                  product.shippingCategory == null
                    ? ' (no shipping category)'
                    : ''
                }: the class-change rules (object ↔ textile) stay off for this product.`
          }
        >
          <Select
            aria-label="Carry-over class"
            value={form.productClass}
            onChange={(e) =>
              setForm((prev) => ({
                ...prev,
                productClass: isProductClass(e.target.value)
                  ? e.target.value
                  : '',
              }))
            }
          >
            <option value="">Unknown</option>
            {PRODUCT_CLASSES.map((productClass) => (
              <option key={productClass} value={productClass}>
                {PRODUCT_CLASS_LABEL[productClass]}
              </option>
            ))}
          </Select>
        </FormField>

        <FormField
          label="Percent"
          hint="Box share of each print area a foreign design is fitted into."
          error={percentError}
        >
          <Input
            type="number"
            min={1}
            max={100}
            step={1}
            aria-label="Carry-over percent"
            value={form.percent}
            placeholder="none (default rules)"
            onChange={(e) =>
              setForm((prev) => ({ ...prev, percent: e.target.value }))
            }
          />
        </FormField>

        <FormField label="Anchor">
          <Select
            aria-label="Carry-over anchor"
            value={form.anchor}
            onChange={(e) =>
              setForm((prev) => ({
                ...prev,
                anchor: isCarryOverAnchor(e.target.value) ? e.target.value : '',
              }))
            }
          >
            <option value="">{anchorPlaceholder(selectedClass)}</option>
            {CARRY_OVER_ANCHORS.map((anchor) => (
              <option key={anchor} value={anchor}>
                {CARRY_OVER_ANCHOR_LABEL[anchor]}
              </option>
            ))}
          </Select>
        </FormField>

        <FormField
          label="Top offset (mm)"
          hint="Applies with anchor top."
          error={offsetError}
        >
          <Input
            type="number"
            min={0}
            step={1}
            aria-label="Carry-over top offset"
            value={form.offsetMm}
            placeholder={`${DEFAULT_TOP_OFFSET_MM} mm (default)`}
            onChange={(e) =>
              setForm((prev) => ({ ...prev, offsetMm: e.target.value }))
            }
          />
        </FormField>
      </div>

      <div className="mt-4 space-y-1 text-xs" data-testid="carry-over-impact">
        {impact.isPending && (
          <p className="text-mute">Counting affected positions…</p>
        )}
        {impact.isError && (
          <p className="text-danger">Could not count affected positions.</p>
        )}
        {impact.data && (
          <>
            <p className="text-ink-2">
              Decides the placement of{' '}
              <span className="font-semibold text-ink">
                {impact.data.targetPositions}
              </span>{' '}
              position(s) on this product and{' '}
              <span className="font-semibold text-ink">
                {impact.data.sourcePositions}
              </span>{' '}
              position(s) of other products where this product is the master, in{' '}
              <span className="font-semibold text-ink">
                {impact.data.designs}
              </span>{' '}
              design(s).
            </p>
            {impact.data.areaOverrides.length > 0 && (
              <p className="text-ink-2">
                Area exceptions exist on: {impact.data.areaOverrides.join(', ')}
                .
              </p>
            )}
            {impact.data.kleinOverridesRemaining > 0 && (
              <p role="alert" className="text-warning">
                Migration retire_klein_override pending:{' '}
                {impact.data.kleinOverridesRemaining} print area(s) still carry
                the retired KLEIN override; the rule reads them as percent 100,
                centred.
              </p>
            )}
          </>
        )}
      </div>

      <div className="mt-4 flex items-center gap-4">
        <Button
          disabled={!canSave}
          loading={save.isPending}
          onClick={handleSave}
        >
          Save carry-over
        </Button>
        <p className="text-xs text-mute">
          Takes effect on cards and in the editor at once; stored mockups and
          listings only after a re-render by the operator.
        </p>
      </div>
    </Card>
  );
}
